"use client";

import type React from 'react';
import { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary capturou um erro:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  const handleRetry = () => {
    onReset();
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: "var(--p-surface-50)" }}>
      <div className="max-w-md w-full mx-4 rounded-2xl shadow-xl" style={{ background: "var(--p-surface-0)" }}>
        <div className="p-6 text-center">
          <AlertCircle className="h-16 w-16 text-error mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--p-text-color)" }}>Ops! Algo deu errado</h2>
          <p className="mb-6" style={{ color: "var(--p-text-muted-color)" }}>
            {error?.message || 'Ocorreu um erro inesperado. Por favor, tente novamente.'}
          </p>
          <div className="flex justify-center gap-2">
            <Button onClick={handleRetry} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              Recarregar Página
            </Button>
            <Button onClick={() => window.history.back()} variant="ghost">
              Voltar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

